import { Dialog } from '@headlessui/react'
import { createContext, useContext, useState } from 'react'
import type { PropsWithChildren } from 'react'

interface BaseModalState {
  isOpen: boolean
}

interface BaseModalDispatch {
  open: () => void
  close: () => void
  onApply: () => any
  handleApply: (callback?: (params?: any) => any) => void
  onClose: () => any
  handleClose: (callback?: (params?: any) => any) => void
}

const BaseModalStateContext = createContext<BaseModalState>(
  {} as BaseModalState,
)
BaseModalStateContext.displayName = 'BaseModalState'

const BaseModalDispatchContext = createContext<BaseModalDispatch>(
  {} as BaseModalDispatch,
)
BaseModalDispatchContext.displayName = 'BaseModalDispatch'

function BaseModalProvider({ children }: PropsWithChildren): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [onApply, setOnApply] = useState<() => void>(() => {
    setIsOpen(false)
  })
  const [onClose, setOnClose] = useState<() => void>(() => {})

  const open = (): void => {
    setIsOpen(true)
  }

  const close = (): void => {
    setIsOpen(false)
  }

  const handleApply = (callback?: (params?: any) => void): void => {
    setOnApply(() => (params?: any) => {
      const ret = !!callback && callback(params)
      if (ret !== false) {
        setIsOpen(false)
      }
    })
  }

  const handleClose = (callback?: (params?: any) => void): void => {
    setOnClose(() => (params?: any) => {
      !!callback && callback(params)
    })
  }

  return (
    <BaseModalStateContext.Provider value={{ isOpen }}>
      <BaseModalDispatchContext.Provider
        value={{ open, close, onApply, handleApply, onClose, handleClose }}
      >
        {children}
      </BaseModalDispatchContext.Provider>
    </BaseModalStateContext.Provider>
  )
}

function useBaseModalState(): BaseModalState {
  try {
    const context = useContext(BaseModalStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useBaseModalState must be used within a BaseModalProvider',
      { cause: error },
    )
  }
}

function useBaseModalDispatch(): BaseModalDispatch {
  try {
    const context = useContext(BaseModalDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useBaseModalDispatch must be used within a BaseModalProvider',
      { cause: error },
    )
  }
}

function BaseModal({
  children,
  isOpen,
  close,
  onClose = (): void => {},
}: PropsWithChildren & {
  isOpen: boolean
  close: () => void
  onClose?: () => void
}): JSX.Element {
  const handleClose = (): void => {
    close()
    onClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="relative z-50"
      initialFocus={undefined}
    >
      {children}
    </Dialog>
  )
}

export {
  BaseModal,
  BaseModalProvider,
  BaseModalStateContext,
  useBaseModalState,
  BaseModalDispatchContext,
  useBaseModalDispatch,
  type BaseModalState,
  type BaseModalDispatch,
}
